// Palette + motion knobs. These were editor-exposed props in the original
// design doc; here they are plain values you can edit or pass into <App />.

export const FIELD_DEFAULTS = {
    breath: 55,    // %  brightness swell amount
    speed: 40,     // %  noise drift rate
    size: 2,       // px base particle size
    density: 84,   // %  grid tightness
    contrast: 60,  // %  noise contrast
};

// Scrolling panels run the full height of the stage and fade at both ends. Each
// fade is a fixed band measured from the container edge, and the content is
// padded past it, otherwise the first and last rows sit inside the band and
// read as dimmed.
export const FADE_TOP = 40;
export const PAD_TOP = 52;
export const FADE_BOTTOM = 40;
export const PAD_BOTTOM = 52;

// Content is bounded by the two fixed marks in the layout, both 60px from their
// edge: the top of the "Jonathan Edward" wordmark, and the "Contact" baseline in
// the nav rail. Content sits inset by its padding, so each container edge
// reaches that much further out.
const STAGE_INSET = 60;
export const PANEL_TOP = STAGE_INSET - PAD_TOP;
export const PANEL_BOTTOM = STAGE_INSET - PAD_BOTTOM;

// Spacing between entries is carried by the rows themselves, as vertical
// padding, rather than by a flex gap. A flex gap belongs to the container, so it
// leaves a dead strip between rows where a hover falls through to nothing; with
// the space inside the rows, every pixel of the list belongs to an entry.
export const ENTRY_GAP = 66;
export const ROW_PAD_Y = ENTRY_GAP / 2;

// Each row already contributes half a gap of its own, so the list's own padding
// is reduced by that much to keep the first and last rows on STAGE_INSET.
export const LIST_PAD_TOP = PAD_TOP - ROW_PAD_Y;
export const LIST_PAD_BOTTOM = PAD_BOTTOM - ROW_PAD_Y;

// Mobile uses one reading column above a persistent bottom navigation bar.
export const MOBILE_QUERY = '(max-width: 760px)';
export const MOBILE_EDGE = 24;
export const MOBILE_PANEL_LEFT = MOBILE_EDGE;
export const MOBILE_PANEL_TOP = 124;
export const MOBILE_PANEL_BOTTOM = 96;
// Tighter than the desktop rhythm: a portrait screen has far less height to
// spend, and rows there are tapped rather than hovered, so they need less air.
export const MOBILE_ROW_PAD_Y = 20;
// Padding plus each row's inset clears the 28px fade at either end.
export const MOBILE_LIST_PAD = 16;
export const MOBILE_FADE_MASK =
    'linear-gradient(to bottom, transparent, #000 28px, #000 calc(100% - 28px), transparent)';

export const FADE_MASK =
    `linear-gradient(to bottom, transparent 0, #000 ${FADE_TOP}px, ` +
    `#000 calc(100% - ${FADE_BOTTOM}px), transparent 100%)`;

export const PALETTES = {
    dark: {
        color: '#f4f4f2',
        accent: '#8fe3b0',
        bg: 0x060708,
        ink: [0.966, 0.976, 0.988],
        alpha: { min: 0.11, max: 0.86 },
        textShadow: '0 1px 22px rgba(0,0,0,.5)',
        // Tight, opaque shadow for small uppercase type, which the wide blur above
        // does nothing for when a bright particle sits directly behind a stroke.
        microShadow: '0 1px 3px rgba(0,0,0,.9), 0 1px 14px rgba(0,0,0,.7)',
        scrim:
            'radial-gradient(120% 115% at 50% 50%, rgba(4,5,7,0) 40%, rgba(4,5,7,.55) 100%), ' +
            'linear-gradient(to bottom, rgba(4,5,7,.62), rgba(4,5,7,.44) 12%, ' +
            'rgba(4,5,7,.14) 24%, rgba(4,5,7,0) 34%, rgba(4,5,7,0) 60%, rgba(4,5,7,.5))',
        // access dot: green = repo you can open, amber = public but no repo, grey = private
        status: { repo: '#6fe39a', public: '#f0c24b', private: 'rgba(244,244,242,.34)' },
        // project marker: amber = still going, green = done and viewable, grey = private
        marker: { ongoing: '#f0c24b', done: '#6fe39a', private: 'rgba(244,244,242,.45)' },
    },
    light: {
        color: '#231f1a',
        accent: '#2f9f74',
        bg: 0xe8e5df,
        ink: [0.102, 0.09, 0.078],
        alpha: { min: 0.13, max: 0.62 },
        textShadow: '0 1px 18px rgba(255,255,255,.5)',
        microShadow: '0 1px 3px rgba(232,229,223,.95), 0 1px 14px rgba(232,229,223,.7)',
        scrim:
            'radial-gradient(120% 115% at 50% 50%, rgba(232,229,223,0) 44%, rgba(232,229,223,.5) 100%), ' +
            'linear-gradient(to bottom, rgba(232,229,223,.6), rgba(232,229,223,.42) 12%, ' +
            'rgba(232,229,223,.12) 24%, rgba(232,229,223,0) 34%)',
        status: { repo: '#2f9f74', public: '#b8860b', private: 'rgba(35,31,26,.32)' },
        marker: { ongoing: '#b8860b', done: '#2f9f74', private: 'rgba(35,31,26,.45)' },
    },
};
